import { Box, Button, Stack, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useTBContext } from '../../../Contexts/useTBStates';
import { TeeSheetDisplay } from '../../TeeSheetDisplay';
import { ScoreCodeCards } from '../../ScoreCards/ScoreCodeCards';
import { ScorecardCards } from '../../ScoreCards/ScorecardCards';
import { effectiveRoundBool } from '../../Functions/applyUniformStartHoleToViewEventData';

interface PlayerManagementPanelProps {
    appDims: any;
    compHeights: any;
}

function scoresReadyForRound(scores: any[], roundValue: number, shotgun: boolean): boolean {
    const roundIdx = roundValue - 1;
    if (scores.length === 0) return false;
    return scores.every((score: any) => {
        const rs = score.roundScores[roundIdx];
        if (rs?.teeTime == null) return false;
        if (shotgun && rs.startingHole == null) return false;
        return true;
    });
}

function computeTeeTimes(scores: any[], registrations: any[], roundValue: number): any[] {
    const roundIdx = roundValue - 1;
    const teeTimes = scores.length > 0
        ? scores.map((score: any) => score.roundScores[roundIdx].teeTime)
        : registrations.map((reg: any) => reg.teeTimes[roundIdx]?.teeTime).filter(Boolean);
    const teeTimesUnique = Array.from(new Set(teeTimes));
    return teeTimesUnique.sort((a: any, b: any) => new Date(a).getTime() - new Date(b).getTime());
}

export const PlayerManagementPanel = ({ appDims, compHeights }: PlayerManagementPanelProps) => {
    const { factor, appTheme, roundValue, viewEvent, viewEventData } = useTBContext();

    const [copiedShow, setCopiedShow] = useState(false);
    const [playerMgmtSection, setPlayerMgmtSection] = useState<'teeSheet' | 'scoreCards' | 'scorecards'>('teeSheet');
    const [teeTimes, setTeeTimes] = useState<any[]>([]);

    const isShotgunRound = Boolean(
        viewEvent && effectiveRoundBool(viewEvent.rounds[roundValue - 1]?.shotgunStart),
    );

    useEffect(() => {
        if (viewEvent && scoresReadyForRound(viewEventData.scores, roundValue, isShotgunRound)) {
            setTeeTimes(computeTeeTimes(viewEventData.scores, viewEventData.registrations, roundValue));
        } else {
            setTeeTimes([]);
        }
    }, [roundValue, viewEventData.scores, viewEventData.registrations, viewEvent, isShotgunRound]);

    if (!viewEvent) return null;

    return (
        <Stack
            direction='column'
            spacing={2}
            sx={{
                width: '95%',
                margin: 2,
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap' justifyContent='space-between' sx={{ flexShrink: 0 }}>
                <Typography variant='h6' sx={{ fontWeight: 500 }}>Player Management</Typography>
                <Stack direction='row' spacing={2} alignItems='center'>
                    <Button
                        variant="outlined"
                        sx={{ width: 'max-content' }}
                        onClick={() => {
                            const emails = viewEventData.registrations
                                .map((reg: any) => reg.playerEmail)
                                .join(';');
                            navigator.clipboard.writeText(emails);
                            setCopiedShow(true);
                            setTimeout(() => setCopiedShow(false), 3000);
                        }}
                    >
                        Copy All Player Emails
                    </Button>
                    {copiedShow && (
                        <Typography variant='subtitle2'
                            sx={{ border: '1px solid #e0e0e0', borderRadius: 2, padding: 0.5 }}
                        >
                            copied!
                        </Typography>
                    )}
                </Stack>
            </Stack>

            <Stack direction='row' spacing={0.75} alignItems='center' sx={{ flexShrink: 0 }}>
                {(['teeSheet', 'scoreCards', 'scorecards'] as const).map((section) => (
                    <Button
                        key={section}
                        size='small'
                        onClick={() => setPlayerMgmtSection(section)}
                        sx={{
                            fontSize: 12 * factor,
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1.5,
                            border: `1.5px solid ${appTheme.palette.primary.main}`,
                            color: playerMgmtSection === section ? appTheme.palette.primary.contrastText : appTheme.palette.primary.main,
                            backgroundColor: playerMgmtSection === section ? appTheme.palette.primary.main : 'transparent',
                            '&:hover': {
                                backgroundColor: playerMgmtSection === section
                                    ? appTheme.palette.primary.dark
                                    : `${appTheme.palette.primary.extraLight}`,
                                borderColor: appTheme.palette.primary.main,
                            },
                        }}
                    >
                        {section === 'teeSheet' ? 'Tee Sheet' : section === 'scoreCards' ? 'Score Codes' : 'Scorecards'}
                    </Button>
                ))}
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', width: '100%', display: 'flex', flexDirection: 'column' }}>
                {playerMgmtSection === 'teeSheet' && (
                    <TeeSheetDisplay
                        registrations={viewEventData.registrations ?? []}
                        teeTimes={teeTimes}
                        roundValue={roundValue}
                        viewEvent={viewEvent}
                        courseData={viewEventData.courseData ?? []}
                        factor={factor}
                    />
                )}
                {playerMgmtSection === 'scoreCards' && (
                    <ScoreCodeCards
                        registrations={viewEventData.registrations ?? []}
                        teeTimes={teeTimes}
                        roundValue={roundValue}
                        viewEvent={viewEvent as Record<string, unknown>}
                        factor={factor}
                    />
                )}
                {playerMgmtSection === 'scorecards' && (
                    <ScorecardCards
                        registrations={viewEventData.registrations ?? []}
                        roundValue={roundValue}
                        viewEvent={viewEvent as Record<string, unknown>}
                        viewEventData={viewEventData}
                        factor={factor}
                    />
                )}
            </Box>
        </Stack>
    );
};
